import { useState } from 'react';
import { media } from '@bb/contracts';
import { Button, Modal } from '@bb/ui';
import type { MediaCrop, MediaPreset, MediaVersionView } from '../../api/client.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: MediaVersionView;
  preset: MediaPreset;
  busy?: boolean;
  error?: string | null;
  onSave: (crop: MediaCrop) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Crop of one rendition preset (BB-52): position and width in pixels of the original; the height follows the
 * preset's aspect, so the crop always fits the channel. The preview shows the selected area over the image.
 */
export function CropDialog({ open, onOpenChange, version, preset, busy, error, onSave }: Props) {
  const definition = media.RENDITION_PRESETS[preset];
  const aspect = definition.aspect ?? version.width / version.height;
  const initial = version.renditions[preset]?.crop ?? { x: 0, y: 0, width: version.width, height: version.height };
  const [draft, setDraft] = useState({ x: String(initial.x), y: String(initial.y), width: String(initial.width) });
  const maxWidth = Math.floor(Math.min(version.width, version.height * aspect));
  // Inputs keep what is typed; the crop used for the preview and the save is the typed one fitted into the image.
  const width = clamp(Math.round(Number(draft.width) || 1), 1, maxWidth);
  const height = definition.aspect === null ? Math.max(1, Math.round((width * initial.height) / initial.width)) : Math.max(1, Math.round(width / aspect));
  const crop: MediaCrop = { width, height, x: clamp(Math.round(Number(draft.x) || 0), 0, version.width - width), y: clamp(Math.round(Number(draft.y) || 0), 0, version.height - height) };
  const pct = (value: number, total: number) => `${(value / total) * 100}%`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Crop: ${definition.label}`}
      description={`Original ${version.width}×${version.height} px. The rendition is made again under a new URL; published messages keep the old one.`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onSave(crop)} disabled={busy}>
            Save crop
          </Button>
        </>
      }
    >
      <div className="crop-preview" style={{ aspectRatio: `${version.width} / ${version.height}` }}>
        <img src={version.url} alt="" />
        <div
          className="crop-preview__area"
          aria-hidden="true"
          style={{ left: pct(crop.x, version.width), top: pct(crop.y, version.height), width: pct(crop.width, version.width), height: pct(crop.height, version.height) }}
        />
      </div>
      <div className="filters" role="group" aria-label="Crop area">
        <label>
          Left (px)
          <input type="number" min={0} max={version.width - crop.width} value={draft.x} onChange={(e) => setDraft({ ...draft, x: e.target.value })} />
        </label>
        <label>
          Top (px)
          <input type="number" min={0} max={version.height - crop.height} value={draft.y} onChange={(e) => setDraft({ ...draft, y: e.target.value })} />
        </label>
        <label>
          Width (px)
          <input type="number" min={1} max={maxWidth} value={draft.width} onChange={(e) => setDraft({ ...draft, width: e.target.value })} />
        </label>
        <span aria-live="polite">
          Crop {crop.width}×{crop.height} px at {crop.x}, {crop.y}
        </span>
      </div>
      {error && <p role="alert">{error}</p>}
    </Modal>
  );
}
